import { Injectable, OnDestroy, signal, computed, inject } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { breakpointMap, breakpoints } from '@shared/layout/break-points';

/**
 * Centralised service for tracking layout dimensions.
 *
 * Uses ResizeObserver to reactively track header and footer heights,
 * enabling dynamic sidenav positioning that adjusts when banners appear/disappear.
 *
 * @example
 * ```typescript
 * // In AppComponent - register elements
 * @ViewChild('appToolbar') appToolbar: ElementRef<HTMLElement>;
 *
 * ngAfterViewInit() {
 *   this.layoutService.registerHeaderElement(this.appToolbar.nativeElement);
 * }
 *
 * // In ProjectComponent - consume signals
 * readonly sidenavTop = this.layoutService.sidenavTop;
 * ```
 */
/**
 * Height of a ResizeObserver entry's BORDER box.
 *
 * `contentRect` is the CONTENT box: it excludes padding and border, so a
 * footer with `padding-bottom: 24px` reports ~24px shorter than the space it
 * actually occupies. Consumers position chrome against the element's real
 * footprint, so they need the border box. `borderBoxSize` is the supported
 * way to get it; `getBoundingClientRect()` is the fallback for older engines.
 *
 * Both branches narrow on `typeof === 'number'` rather than `> 0`. A `> 0`
 * test conflates "this engine does not report the value" with "this element
 * is genuinely zero tall": a header or footer collapsed to `height: 0` still
 * fires the observer with `blockSize: 0`, and returning `fallback` there
 * would leave consumers reserving a phantom 60-64px of chrome that is no
 * longer on screen. `fallback` is for a missing measurement only.
 *
 * The fallback reads `entry.contentRect`, which the observer already carries,
 * rather than calling `getBoundingClientRect()`. A bounding-rect read inside a
 * ResizeObserver callback forces a synchronous layout and can re-trigger the
 * observer on an element whose height depends on its own content
 * ("ResizeObserver loop completed with undelivered notifications"); the entry
 * field costs nothing and cannot loop. It is the CONTENT box, so it drops the
 * padding this function exists to include — a degraded last resort for an
 * engine without `borderBoxSize`, never the normal path. Do not promote it.
 */
function borderBoxHeight(entry: ResizeObserverEntry, fallback: number): number {
  const boxed = entry.borderBoxSize?.[0]?.blockSize;
  if (typeof boxed === 'number') {
    return boxed;
  }
  const measured = entry.contentRect?.height;
  return typeof measured === 'number' ? measured : fallback;
}

@Injectable({ providedIn: 'root' })
export class LayoutService implements OnDestroy {
  /** Default header height (navbar without banners) */
  private static readonly DEFAULT_HEADER_HEIGHT = 64;
  /** Default footer height */
  private static readonly DEFAULT_FOOTER_HEIGHT = 60;

  private readonly _headerHeight = signal(LayoutService.DEFAULT_HEADER_HEIGHT);
  private readonly _footerHeight = signal(LayoutService.DEFAULT_FOOTER_HEIGHT);
  private readonly _breakpointObserver = inject(BreakpointObserver);

  private readonly compactApplicationShellQuery =
    breakpointMap[breakpoints.lt_lg].mediaQuery;

  private _headerObserver: ResizeObserver | null = null;
  private _footerObserver: ResizeObserver | null = null;

  /** Current header height in pixels (including any banners) */
  readonly headerHeight = this._headerHeight.asReadonly();

  /** Current footer height in pixels */
  readonly footerHeight = this._footerHeight.asReadonly();

  /** Combined header + footer height (useful for calculating available viewport) */
  readonly layoutOffset = computed(
    () => this._headerHeight() + this._footerHeight()
  );

  /** Top position for sidenav (equals header height) */
  readonly sidenavTop = computed(() => this._headerHeight());

  /** Whether the header and project navigation should use their compact layout. */
  readonly compactApplicationShell = toSignal(
    this._breakpointObserver
      .observe(this.compactApplicationShellQuery)
      .pipe(map((state) => state.matches)),
    {
      initialValue: this._breakpointObserver.isMatched(
        this.compactApplicationShellQuery
      ),
    }
  );

  /**
   * Register the header element for height tracking.
   * Should be called from AppComponent's ngAfterViewInit.
   *
   * @param element The header container element (includes navbar + any banners)
   */
  registerHeaderElement(element: HTMLElement): void {
    // Disconnect any existing observer
    this._headerObserver?.disconnect();

    this._headerObserver = new ResizeObserver((entries) => {
      // Zoneless: this ResizeObserver callback runs outside Angular's zone, and
      // a zone.run() wrapper is NOT a no-op under today's zone bootstrap --
      // it still guarantees a zone tick for out-of-zone callbacks. Deletion
      // is safe anyway because the write below is to a writable signal:
      // signal writes notify Angular's ChangeDetectionScheduler directly,
      // which has been zone-independent since v16, so CD is scheduled with
      // or without the wrapper, both today and after the zoneless flip.
      const entry = entries[0];
      this._headerHeight.set(
        entry
          ? borderBoxHeight(entry, LayoutService.DEFAULT_HEADER_HEIGHT)
          : LayoutService.DEFAULT_HEADER_HEIGHT
      );
    });

    this._headerObserver.observe(element, { box: 'border-box' });
    // Initial value uses offsetHeight (border box) so it matches what the
    // observer reports; clientHeight would exclude the border.
    //
    // Taken as measured, with no `||` fallback: `offsetHeight` is always a
    // number, so `||` was not guarding against an absent reading — it was
    // rewriting a real 0 into 64. An element that is laid out gets its
    // ResizeObserver callback on the same frame and corrects any transient
    // pre-paint 0; an element that is `display: none` never fires the
    // observer at all, and for that one 0 is the right answer, because it
    // occupies no space for chrome to be positioned against.
    this._headerHeight.set(element.offsetHeight);
  }

  /**
   * Register the footer element for height tracking.
   * Should be called from AppComponent's ngAfterViewInit.
   *
   * @param element The footer container element
   */
  registerFooterElement(element: HTMLElement): void {
    // Disconnect any existing observer
    this._footerObserver?.disconnect();

    this._footerObserver = new ResizeObserver((entries) => {
      // Zoneless: this ResizeObserver callback runs outside Angular's zone, and
      // a zone.run() wrapper is NOT a no-op under today's zone bootstrap --
      // it still guarantees a zone tick for out-of-zone callbacks. Deletion
      // is safe anyway because the write below is to a writable signal:
      // signal writes notify Angular's ChangeDetectionScheduler directly,
      // which has been zone-independent since v16, so CD is scheduled with
      // or without the wrapper, both today and after the zoneless flip.
      const entry = entries[0];
      this._footerHeight.set(
        entry
          ? borderBoxHeight(entry, LayoutService.DEFAULT_FOOTER_HEIGHT)
          : LayoutService.DEFAULT_FOOTER_HEIGHT
      );
    });

    this._footerObserver.observe(element, { box: 'border-box' });
    // Initial value uses offsetHeight (border box) so it matches what the
    // observer reports; clientHeight would exclude the border. Taken as
    // measured, with no `||` fallback — see registerHeaderElement.
    this._footerHeight.set(element.offsetHeight);
  }

  ngOnDestroy(): void {
    this._headerObserver?.disconnect();
    this._footerObserver?.disconnect();
  }
}
